import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

import {
  Alert,
  Badge,
  Button,
  Card,
  LoadingBlock,
  PageHeader,
  TextField,
} from "../../components/ui";
import { useToast } from "../../components/Toast";
import { api, applyFieldErrors, errorMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useConfig } from "../../lib/config";
import { formatDateTime } from "../../lib/format";
import { changePasswordSchema } from "../auth/schemas";

interface SessionRow {
  id: string;
  created_at: string;
  last_seen_at: string;
  ip_address: string | null;
  user_agent: string | null;
  current: boolean;
}

function MfaSetup() {
  const { user, refresh } = useAuth();
  const [setup, setSetup] = useState<{
    secret: string;
    otpauth_uri: string;
    qr_svg_data_uri: string;
  } | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!user) return null;
  if (codes)
    return (
      <Alert tone="success" title="Two-step verification is on">
        <p>
          Save these recovery codes somewhere safe. Each one works once if you lose your phone. They
          will not be shown again.
        </p>
        <ul className="mt-2 grid grid-cols-2 gap-1 font-mono">
          {codes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      </Alert>
    );
  if (user.mfa_enabled)
    return (
      <p className="flex items-center gap-2">
        Two-step verification <Badge tone="ok">On</Badge>
      </p>
    );

  return (
    <div className="flex flex-col gap-4">
      {user.mfa_setup_required && (
        <Alert tone="warning" title="Required for staff accounts">
          Turn on two-step verification to use staff and admin tools.
        </Alert>
      )}
      {error && <Alert tone="error">{error}</Alert>}
      {!setup ? (
        <div>
          <p className="mb-3 text-ink-muted">
            Use an authenticator app (such as Microsoft Authenticator or Google Authenticator) to
            add a second step when you sign in.
          </p>
          <Button
            busy={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                setSetup(await api.post("/auth/mfa/setup"));
              } catch (err) {
                setError(errorMessage(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            Set up two-step verification
          </Button>
        </div>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            try {
              const res = await api.post<{ recovery_codes: string[] }>("/auth/mfa/enable", {
                code,
              });
              setCodes(res.recovery_codes);
              await refresh();
            } catch (err) {
              setError(errorMessage(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          <ol className="list-decimal space-y-2 pl-5 text-sm">
            <li>Open your authenticator app and choose to add an account.</li>
            <li>Scan this QR code with your phone's camera in the app.</li>
            <li>Enter the 6-digit code the app shows.</li>
          </ol>
          <img
            src={setup.qr_svg_data_uri}
            alt="QR code for setting up two-step verification in your authenticator app"
            className="h-52 w-52 rounded border border-line bg-white p-1"
          />
          <p className="text-sm text-ink-muted">
            Can't scan the code? Enter this setup key in the app instead:
          </p>
          <code className="break-all rounded bg-canvas p-3 font-mono text-base tracking-wider">
            {setup.secret}
          </code>
          <p className="text-sm text-ink-muted">
            On this phone, you can also{" "}
            <a className="text-brand-700 underline" href={setup.otpauth_uri}>
              open the setup link
            </a>{" "}
            in your app.
          </p>
          <TextField
            label="6-digit code from the app"
            inputMode="numeric"
            autoComplete="one-time-code"
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <Button type="submit" busy={busy}>
            Turn on
          </Button>
        </form>
      )}
    </div>
  );
}

function ChangePassword() {
  const { notify } = useToast();
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    setError: setFieldError,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof changePasswordSchema>>({
    resolver: zodResolver(changePasswordSchema),
  });
  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={handleSubmit(async (v) => {
        setError(null);
        try {
          await api.post("/auth/change-password", {
            current_password: v.current_password,
            new_password: v.new_password,
          });
          reset();
          notify("Your password was changed.");
        } catch (err) {
          if (!applyFieldErrors(err, setFieldError)) setError(errorMessage(err));
        }
      })}
    >
      {error && <Alert tone="error">{error}</Alert>}
      <TextField
        label="Current password"
        type="password"
        autoComplete="current-password"
        required
        error={errors.current_password?.message}
        {...register("current_password")}
      />
      <TextField
        label="New password"
        type="password"
        autoComplete="new-password"
        required
        error={errors.new_password?.message}
        {...register("new_password")}
      />
      <TextField
        label="Repeat new password"
        type="password"
        autoComplete="new-password"
        required
        error={errors.confirm_password?.message}
        {...register("confirm_password")}
      />
      <Button type="submit" busy={isSubmitting} className="self-start">
        Change password
      </Button>
    </form>
  );
}

function Sessions() {
  const qc = useQueryClient();
  const { data: config } = useConfig();
  const tz = config?.business_timezone ?? "UTC";
  const { data, isLoading } = useQuery({
    queryKey: ["sessions"],
    queryFn: () => api.get<SessionRow[]>("/auth/sessions"),
  });
  if (isLoading) return <LoadingBlock />;
  return (
    <ul className="divide-y divide-line">
      {data?.map((s) => (
        <li
          key={s.id}
          className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <span className="text-sm">
            <span className="font-semibold">{s.user_agent?.slice(0, 60) ?? "Unknown device"}</span>
            {s.current && <Badge tone="brand"> This device</Badge>}
            <br />
            <span className="text-ink-muted">Last active {formatDateTime(s.last_seen_at, tz)}</span>
          </span>
          {!s.current && (
            <Button
              variant="secondary"
              onClick={async () => {
                await api.del(`/auth/sessions/${s.id}`);
                await qc.invalidateQueries({ queryKey: ["sessions"] });
              }}
            >
              Sign out
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}

export function SecurityPage() {
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Sign-in and security" />
      <Card>
        <h2 className="mb-3 text-lg font-bold">Two-step verification</h2>
        <MfaSetup />
      </Card>
      <Card>
        <h2 className="mb-3 text-lg font-bold">Change password</h2>
        <ChangePassword />
      </Card>
      <Card>
        <h2 className="mb-3 text-lg font-bold">Signed-in devices</h2>
        <Sessions />
      </Card>
    </div>
  );
}
